import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Loader2, UserMinus, RefreshCw, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminParticipantsPage = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadRegistrations();
  }, []);

  const loadRegistrations = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/registrations`, { withCredentials: true });
      setRegistrations(res.data);
    } catch (err) {
      setError('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const removeParticipant = async (participantId) => {
    if (!window.confirm(`Remove participant ${participantId}? This cannot be undone.`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/remove-participant`, { participant_id: participantId }, { withCredentials: true });
      setSuccess('Participant removed');
      loadRegistrations();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove participant');
    }
  };

  const filteredRegistrations = registrations.filter(r => {
    const q = search.toLowerCase();
    return !q || 
      (r.child_name || '').toLowerCase().includes(q) ||
      (r.participant_id || '').toLowerCase().includes(q) ||
      (r.parent_email || '').toLowerCase().includes(q);
  });

  const paidCount = registrations.filter(r => r.payment_status === 'paid').length;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Participants
        </h1>
        <p className="text-sm text-[#64748b] mt-1">Registered list, codes, and seat count.</p>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card-olympiad p-4 text-center">
          <p className="text-2xl font-bold text-[#0f172a]">{registrations.length}</p>
          <p className="text-xs font-semibold text-[#64748b]">Total Registered</p>
        </div>
        <div className="card-olympiad p-4 text-center">
          <p className="text-2xl font-bold text-[#22c55e]">{paidCount}</p>
          <p className="text-xs font-semibold text-[#64748b]">Confirmed (Paid)</p>
        </div>
        <div className="card-olympiad p-4 text-center">
          <p className="text-2xl font-bold text-[#d97706]">{registrations.length - paidCount}</p>
          <p className="text-xs font-semibold text-[#64748b]">Pending</p>
        </div>
      </div>

      {error && (
        <div className="bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] px-4 py-3 rounded-2xl mb-4 text-sm font-medium">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-[#dcfce7] border-2 border-[#86efac] text-[#16a34a] px-4 py-3 rounded-2xl mb-4 text-sm font-medium">
          {success}
        </div>
      )}

      {/* Search & Refresh */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#94a3b8] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-olympiad pl-10"
            placeholder="Search by name, ID, or email..."
            data-testid="participants-search"
          />
        </div>
        <button
          onClick={loadRegistrations}
          className="p-3 rounded-xl text-[#2565eb] hover:bg-[#eff6ff] transition-colors border-2 border-[#e2e8f0]"
          data-testid="refresh-participants"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#2565eb] animate-spin" />
        </div>
      ) : filteredRegistrations.length === 0 ? (
        <div className="card-olympiad p-12 text-center">
          <Users className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
          <p className="text-[#64748b] font-medium">No participants found</p>
        </div>
      ) : (
        <div className="card-olympiad overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="participants-table">
              <thead>
                <tr className="bg-[#f8fafc] border-b-2 border-[#e2e8f0]">
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase tracking-wide">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase tracking-wide">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase tracking-wide">Event Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase tracking-wide">Parent Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase tracking-wide">Payment</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {filteredRegistrations.map((reg) => (
                  <tr key={reg.participant_id} className="hover:bg-[#f8fafc] transition-colors">
                    <td className="px-4 py-3 text-sm font-semibold text-[#0f172a]">{reg.child_name}</td>
                    <td className="px-4 py-3 text-sm text-[#64748b] font-mono">{reg.participant_id}</td>
                    <td className="px-4 py-3 text-sm text-[#64748b] font-mono">{reg.event_code || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[#64748b] font-mono">{reg.parent_access_code || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        reg.payment_status === 'paid'
                          ? 'bg-[#dcfce7] text-[#16a34a]'
                          : 'bg-[#fef3c7] text-[#d97706]'
                      }`}>
                        {reg.payment_status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        reg.codes_active
                          ? 'bg-[#dcfce7] text-[#16a34a]'
                          : 'bg-[#f1f5f9] text-[#64748b]'
                      }`}>
                        {reg.codes_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => removeParticipant(reg.participant_id)}
                        className="p-2 rounded-xl text-[#ef4444] hover:bg-[#fef2f2] transition-colors"
                        title="Remove participant"
                        data-testid={`remove-${reg.participant_id}`}
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminParticipantsPage;
